// SVG-based text width measurer for accurate Recharts measurements
// Creates temporary SVG elements to measure text exactly as it will appear in charts

let _svg: SVGSVGElement | null = null;
let _text: SVGTextElement | null = null;

function getSvgElements() {
  if (!_svg) {
    // Create a hidden SVG element for measurements
    _svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    _svg.style.position = 'absolute';
    _svg.style.visibility = 'hidden';
    _svg.style.width = '0';
    _svg.style.height = '0';
    _svg.style.overflow = 'hidden';
    document.body.appendChild(_svg);

    _text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    _svg.appendChild(_text);
  }
  return { svg: _svg, text: _text! };
}

/**
 * Measures the width of text as it will appear in SVG
 * @param text - The text to measure
 * @param fontOrSize - Either a full CSS font string (e.g., "12px Inter") or a numeric font size
 * @param fontFamily - Optional font family (required if fontOrSize is a number)
 * @returns The width of the text in pixels
 */
export function measureTextWidth(
  text: string,
  fontOrSize: string | number,
  fontFamily?: string
): number {
  if (typeof window === 'undefined') return 0; // SSR safety

  const { text: textEl } = getSvgElements();
  if (!textEl) return 0;

  // Support both formats: full font string OR fontSize + fontFamily
  if (typeof fontOrSize === 'number' && fontFamily) {
    textEl.style.fontSize = `${fontOrSize}px`;
    textEl.style.fontFamily = fontFamily;
  } else {
    // Parse font string (e.g., "12px Inter, sans-serif")
    const fontStr = String(fontOrSize);
    const parts = fontStr.split(' ');
    if (parts.length >= 2) {
      textEl.style.fontSize = parts[0];
      textEl.style.fontFamily = parts.slice(1).join(' ');
    } else {
      textEl.style.font = fontStr;
    }
  }

  textEl.textContent = text;

  // Use getBBox for accurate measurement
  try {
    const bbox = textEl.getBBox();
    return Math.ceil(bbox.width);
  } catch (e) {
    // Fallback to getComputedTextLength if getBBox fails
    try {
      return Math.ceil(textEl.getComputedTextLength());
    } catch (e2) {
      // Final fallback: estimate based on character count
      return Math.ceil(
        text.length * (typeof fontOrSize === 'number' ? fontOrSize * 0.6 : 8)
      );
    }
  }
}

/**
 * Finds the maximum width among an array of strings
 * @param strings - Array of strings or numbers to measure
 * @param fontOrSize - Either a full CSS font string or a numeric font size
 * @param fontFamily - Optional font family (required if fontOrSize is a number)
 * @returns The width of the longest string in pixels
 */
export function maxTextWidth(
  strings: (string | number)[],
  fontOrSize: string | number,
  fontFamily?: string
): number {
  let max = 0;
  for (const s of strings) {
    const w = measureTextWidth(String(s), fontOrSize, fontFamily);
    if (w > max) max = w;
  }
  return max;
}

/**
 * Cleanup function to remove the hidden SVG element
 * Call this when your app unmounts if needed
 */
export function cleanupTextMeasurement() {
  if (_svg && _svg.parentNode) {
    _svg.parentNode.removeChild(_svg);
    _svg = null;
    _text = null;
  }
}
